<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :items="items" class="mc-page-three" />

      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end">
          <McButtonBack />
        </div>
      </div>
    </div>

    <div class="clearfix mb-4">
      <h4 class="card-title float-start">
        {{ $t('master.service.serviceInfo') }}
      </h4>
      <div class="float-end">
        <McButton
          variant="secondary"
          class="me-2"
          @click="cancel"
          icon="icon-fill_close"
          :text="$t('global.cancel')"
        />

        <McButton
          variant="primary"
          type="button"
          @click="save"
          :disabled="disableButton"
          icon="icon-outline_save"
          :text="$t('global.save')"
        />
      </div>
    </div>

    <FormulateForm v-model="newData" ref="formRef">
      <b-skeleton-wrapper :loading="isEdit && loading">
        <template #loading>
          <b-card>
            <b-row class="my-4">
              <b-col sm="12" md="4">
                <b-skeleton width="100%" height="50px" />
              </b-col>
              <b-col sm="12" md="4">
                <b-skeleton width="100%" height="50px" />
              </b-col>
              <b-col sm="12" md="4">
                <b-skeleton width="100%" height="50px" />
              </b-col>
            </b-row>
          </b-card>
        </template>
        <b-card>
          <b-row class="mb-3">
            <b-col md="4">
              <FormulateInput
                name="name"
                required
                type="text"
                :label="$t('master.service.serviceName')"
                :placeholder="`${$t('global.form.enter')} ${$t('master.service.serviceName')}`"
                validation-behavior="blur"
                error-behavior="blur"
                validation="required:trim"
              />
            </b-col>
            <b-col md="4">
              <FormulateInput
                name="description"
                required
                type="text"
                :label="$t('master.service.description')"
                :placeholder="`${$t('global.form.enter')} ${$t('master.service.description')}`"
                validation-behavior="blur"
                error-behavior="blur"
              />
            </b-col>
            <b-col md="4">
              <FormulateInput
                type="text"
                name="fee"
                required
                :label="$t('master.service.price')"
                :placeholder="`${$t('global.form.enter')} ${$t('master.service.price')}`"
                validation-behavior="blur"
                error-behavior="blur"
                validation="number"
                prefix
              >
                <template #prefix>
                  <div class="formulate-input-prefix">
                    <span>Rp</span>
                  </div></template
                >
              </FormulateInput>
            </b-col>
          </b-row>
        </b-card>
      </b-skeleton-wrapper>
    </FormulateForm>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import { CREATE_LABOUR, GET_LABOUR, UPDATE_LABOUR } from '@/services/api/master/services'

export default {
  page: {
    title: 'Servis',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McButtonBack,
  },

  apollo: {
    labour: {
      query: GET_LABOUR,
      fetchPolicy: 'no-cache',
      manual: true,
      skip() {
        return !this.$route.params.id
      },
      variables() {
        return {
          filter: {
            labourId: Number(this.$route.params.id),
          },
        }
      },
      result({ data, loading }) {
        this.loading = true
        if (loading || !data) return

        if (data) {
          const res = data.labour.labour

          this.newData = {
            name: res.name,
            description: res.description,
            fee: Number(res.fee),
          }

          this.oldData = { ...this.newData }
          this.loading = false
        }
      },
    },
  },

  data() {
    return {
      oldData: {},
      newData: {
        name: null,
        description: null,
        fee: null,
      },
      disableButton: true,
      loading: true,
    }
  },

  computed: {
    title() {
      return this.$route.params.id === undefined
        ? this.$t('master.service.addNewService')
        : this.$t('master.service.detailService')
    },

    items() {
      return [
        {
          text: this.$t('menuitems.masterData.text'),
          to: '/master',
        },
        {
          text: this.$t('master.maintenance'),
          to: '/master/service',
        },
        {
          text:
            this.$route.params.id === undefined
              ? this.$t('master.service.addNewService')
              : this.$t('master.service.detailService'),
        },
      ]
    },
  },

  watch: {
    newData: {
      deep: true,
      handler(value) {
        if (this.isEdit) {
          if (Object.entries(this.oldData).toString() !== Object.entries(value).toString()) {
            this.disableButton = false
          } else this.disableButton = true
        } else this.disableButton = false
      },
    },
    '$route.params.id': {
      handler(value) {
        if (value) {
          this.isEdit = true
        }
      },
    },
  },

  created() {
    if (this.$route.params.id) {
      this.isEdit = true
    }
  },

  methods: {
    async save() {
      if (this.$refs.formRef.hasErrors) {
        this.$refs.formRef.showErrors()
        return
      }

      if (this.isEdit) {
        const res = await this.$apollo.mutate({
          mutation: UPDATE_LABOUR,
          variables: {
            input: {
              labourId: Number(this.$route.params.id),
              oldData: this.oldData,
              newData: {
                name: this.newData.name,
                description: this.newData.description,
                fee: Number(this.newData.fee),
              },
            },
          },
        })

        if (!res.data.updateLabour.isSuccessful) {
          this.$mcToast.error(res.data.updateLabour.message)
          return
        } else this.$mcToast.success(this.$t('global.alert.text.changed'))
      } else {
        const res = await this.$apollo.mutate({
          mutation: CREATE_LABOUR,
          variables: {
            input: {
              name: this.newData.name,
              description: this.newData.description,
              fee: Number(this.newData.fee),
            },
          },
        })

        if (!res.data.createLabour.isSuccessful) {
          this.$mcToast.error(res.data.createLabour.message)
          return
        } else {
          this.$mcToast.success(this.$t('global.alert.text.added'))

          this.$router.push({
            name: 'Update Master Service Maintenance',
            params: { id: res.data.createLabour.createLabour.labourId },
          })
        }
      }
    },

    handleBack() {
      this.cancel()
      this.$router.push({ name: 'Master Service' })
    },

    cancel() {
      if (this.isEdit) {
        this.newData = this.oldData
      } else {
        this.newData = {
          name: null,
          description: null,
          fee: null,
        }
      }
    },
  },
}
</script>
